import { breakpoint } from '../style/variables.js';
import { PATHNAME_PREFIXES_WITHOUT_DEFAULT_LANG } from '../resource/langPath.js';

const ERROR_RECOVERY_SCRIPT_ID = 'error-recovery';
const ERROR_RECOVERY_TOAST_ID = 'error-recovery-toast';

export const errorRecovery = ({
  enableErrorRecovery,
  errorRecoveryResponseTimeout = 10000,
  maxErrorResourceCount = 3,
} = {}) => {
  if (!enableErrorRecovery) return '';

  return /* html */ `
    <script type="text/javascript" id="${ERROR_RECOVERY_SCRIPT_ID}">
      (function() {
        const MAX_ERROR_RESOURCE_COUNT = ${maxErrorResourceCount};
        const ERROR_RECOVERY_RESPONSE_TIMEOUT_MSEC = ${errorRecoveryResponseTimeout};
        const ERROR_RECOVERY_TOAST_ID = '${ERROR_RECOVERY_TOAST_ID}';
        let errorResourceCount = 0;
        let isReloading = false;
        let hasSyntaxError = false;
        let syntaxErrorMessage = '';
        const url = new URL(window.location.href);

        // Detect SyntaxErrors from script parsing failures
        window.onerror = (message, source, lineno, colno, error) => {
          if (
            (error && error instanceof SyntaxError) ||
            (typeof message === 'string' && /SyntaxError/i.test(message))
          ) {
            hasSyntaxError = true;
            syntaxErrorMessage = (error && error.message) || message || '';
          }
        };

        // Browser support check
        try {
          var hasErrorCause = false;
          try {
            hasErrorCause = 'cause' in new Error('x', { cause: 1 });
          } catch (e) {
            hasErrorCause = false;
          }

          var hasAspectRatio =
            typeof window.CSS === 'object' &&
            null !== window.CSS &&
            typeof window.CSS.supports === 'function' &&
            window.CSS.supports('aspect-ratio', '1 / 1');

          if (!(hasErrorCause && hasAspectRatio)) {
            showErrorRecoveryToast('browser');
          }
        } catch (e) {}

        window.addEventListener(
          'error',
          e => {
            const { target } = e;
            if (target.tagName) {
              const tag = target.tagName.toLowerCase();
              let targetUrl = {};
              try{
                targetUrl = new URL(target.src);
              } catch(_) {}

              if (
                tag === 'script' &&
                targetUrl.hostname === location.hostname &&
                /\\.js$/.test(targetUrl.href)
              ) {
                console.debug('Script resource error: ', target.src);
                errorResourceCount += 1;
                if (errorResourceCount >= MAX_ERROR_RESOURCE_COUNT && !isReloading) {
                  console.debug('Show toast');
                  isReloading = true;
                  const reason = hasSyntaxError ? syntaxErrorMessage : '';
                  showErrorRecoveryToast(reason);
                }
              }
            }
          },
          true
        );

        function reloadPage() {
          url.searchParams.set('clear', 1);
          history.replaceState(null, '', url.href);
          location.reload(true);
        }

        function dismissToast() {
          const toast = document.getElementById(ERROR_RECOVERY_TOAST_ID);
          if (toast) {
            toast.style.animation = 'fadeOut 0.3s ease-in-out forwards';
            setTimeout(() => {
              toast.style.display = 'none';
              toast.style.animation = '';
            }, 300);
          }
        }

        const WORDINGS = {
          browser: {
            'zh-tw': '您的瀏覽器版本不支援，請更新至最新版本',
            'zh-cn': '您的浏览器版本不支持，请更新至最新版本',
            ja: 'お使いのブラウザはサポートされていません。最新バージョンに更新してください',
            th: 'เบราว์เซอร์ของคุณไม่รองรับ กรุณาอัปเดตเป็นเวอร์ชันล่าสุด',
            vi: 'Trình duyệt của bạn không được hỗ trợ. Vui lòng cập nhật lên phiên bản mới nhất',
            ko: '브라우저가 지원되지 않습니다. 최신 버전으로 업데이트해 주세요',
            en: 'Your browser is not supported. Please update to the latest version',
          },
          browserReason: {
            'zh-tw': '本站支持 Safari 15 或其他同等級瀏覽器版本',
            'zh-cn': '本站支持 Safari 15 或其他同等级浏览器版本',
            ja: '当サイトは Safari 15 以上、またはそれに相当するブラウザに対応しています',
            th: 'เว็บไซต์นี้รองรับ Safari 15 หรือเบราว์เซอร์อื่นในระดับเดียวกัน',
            vi: 'Trang web hỗ trợ Safari 15 hoặc các trình duyệt tương đương khác',
            ko: '본 사이트는 Safari 15 이상 또는 그에 상응하는 브라우저를 지원합니다',
            en: 'This site supports Safari 15 or other equivalent browser versions',
          },
          network: {
            'zh-tw': '網路連線不穩定或已中斷',
            'zh-cn': '网络连接不稳定或已中断',
            ja: 'ネットワーク接続が不安定または切断されています',
            th: 'การเชื่อมต่อเครือข่ายไม่เสถียรหรือถูกตัดการเชื่อมต่อ',
            vi: 'Kết nối mạng không ổn định hoặc đã bị ngắt',
            ko: '네트워크 연결이 불안정하거나 끊어졌습니다',
            en: 'Network connection is unstable or disconnected',
          },
        };

        // Mirror the i18n detector order: cookie -> path -> <html lang>
        // (see languageDetectorOption in i18nClient.js).
        // The device language is a backstop only; the app should never use it.
        function getSiteLanguage() {
          var cookieLanguage = (document.cookie.match(/(?:^|;\\s*)i18next=([^;]*)/) || [])[1];
          if (cookieLanguage) return cookieLanguage;
          var pathLanguage = (location.pathname.split('/')[1] || '').toLowerCase();
          if (${JSON.stringify(PATHNAME_PREFIXES_WITHOUT_DEFAULT_LANG.map(prefix => prefix.toLowerCase()))}.indexOf(pathLanguage) !== -1) return pathLanguage;
          return document.documentElement.lang || navigator.language || '';
        }

        // Chrome/Firefox: zh-tw, zh-hk, zh-cn, zh-sg
        // Safari/Apple:   zh-hant, zh-hant-tw, zh-hans, zh-hans-cn
        function getLangWording(type) {
          var lang = getSiteLanguage().toLowerCase();
          // The URL path uses 'cn' for Simplified Chinese (see langPath.js)
          if (lang === 'cn') lang = 'zh-cn';
          var map = WORDINGS[type];
          if (!map) return '';
          if (map[lang]) return map[lang];
          // Chinese: distinguish traditional vs simplified
          if (lang.startsWith('zh')) {
            if (/^zh-(cn|sg|hans)/.test(lang)) return map['zh-cn'] || map.en;
            return map['zh-tw'] || map.en;
          }
          return map[lang.split('-')[0]] || map.en;
        }

        function getWording(reason) {
          if (!reason && window.__ERROR_RECOVERY_WORDING__) {
            return window.__ERROR_RECOVERY_WORDING__;
          }
          var type = reason ? 'browser' : 'network';
          return getLangWording(type);
        }

        function createErrorRecoveryToast(reason) {
          const width = window.innerWidth;
          let toastStyleCssText = 'position: fixed; z-index: 999; max-width: 100%; animation: fadeIn 0.3s ease-in-out;';

          if (width <= ${breakpoint.mobile}) {
            toastStyleCssText += 'width: calc(100% - 24px); bottom: 20px; left: 50%; transform: translateX(-50%); display: flex; justify-content: center;';
          } else if (${breakpoint.mobile} < width && width <= ${breakpoint.tablet}) {
            toastStyleCssText += 'width: 528px; bottom: 20px; left: 50%; transform: translateX(-50%); display: flex; justify-content: center;';
          } else {
            toastStyleCssText += 'width: 528px; bottom: 20px; left: 50%; transform: translateX(-50%); display: flex; justify-content: center;';
          }

          const wording = getWording(reason);
          const isBrowserError = !!reason;

          const toast = document.createElement('div');
          toast.id = ERROR_RECOVERY_TOAST_ID;
          // Browser-unsupported toast must persist: the browser is too old no
          // matter whether the main bundle boots, so clearErrorRecovery (which
          // hides the timeout/resource toasts once the app starts) must skip it.
          if (isBrowserError) toast.dataset.persistent = '1';
          toast.style.cssText = toastStyleCssText;

          const wrapper = document.createElement('div');
          wrapper.style.cssText = 'width: 100%; max-width: 528px; min-height: 48px; border-radius: 8px; background-color: white; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); overflow: hidden; display: flex; flex-direction: column;';

          const notificationContent = document.createElement('div');
          notificationContent.style.cssText = 'display: flex; padding: 12px 16px; position: relative;';

          const iconWrapper = document.createElement('div');
          iconWrapper.style.cssText = 'display: flex; align-items: flex-start; margin-right: 12px; padding-top: 2px;';

          const infoIcon = document.createElementNS("https://www.w3.org/2000/svg", "svg");
          infoIcon.setAttributeNS(null, 'width', '18');
          infoIcon.setAttributeNS(null, 'height', '18');
          infoIcon.setAttributeNS(null, 'fill', 'none');
          infoIcon.innerHTML = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="https://www.w3.org/2000/svg"><path d="M9 0 C4.030 0 0 4.030 0 9 C0 13.970 4.030 18 9 18 C13.970 18 18 13.970 18 9 C18 4.030 13.970 0 9 0 Z" fill="#CF0404"/><path d="M5.677 5.760L11.907 12.240" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.907 5.760L5.677 12.240" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          iconWrapper.appendChild(infoIcon);

          const textContent = document.createElement('div');
          textContent.style.cssText = 'flex: 1; display: flex; justify-content: end; flex-direction: column; overflow: hidden;';

          const title = document.createElement('div');
          title.style.cssText = 'font-size: 14px; font-weight: 600; line-height: 1.5; color: #000; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;';
          title.textContent = wording;
          textContent.appendChild(title);

          var reasonText = isBrowserError ? getLangWording('browserReason') : reason;
          if (reasonText) {
            const reasonEl = document.createElement('div');
            reasonEl.style.cssText = 'font-size: 12px; line-height: 1.4; color: #666; margin-top: 4px;';
            reasonEl.textContent = reasonText;
            textContent.appendChild(reasonEl);
          }

          notificationContent.appendChild(iconWrapper);
          notificationContent.appendChild(textContent);

          const buttonGroup = document.createElement('div');
          buttonGroup.style.cssText = 'display: flex; justify-content: flex-end; padding: 0 16px 12px; gap: 8px; min-height: 38px;';

          if (isBrowserError) {
            const okButton = document.createElement('button');
            okButton.style.cssText = 'border: none; border-radius: 4px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; background-color: #CF0404; padding: 2px 22px; color: white; font-size: 12px;';
            okButton.textContent = 'OK';
            okButton.addEventListener('click', dismissToast);
            buttonGroup.appendChild(okButton);
          } else {
            const primaryButton = document.createElement('button');
            primaryButton.style.cssText = 'border: none; border-radius: 4px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; background-color: #CF0404; padding: 2px 22px; color: white; font-size: 12px;';
            primaryButton.textContent = 'Retry';
            primaryButton.addEventListener('click', reloadPage);

            const dismissButton = document.createElement('button');
            dismissButton.style.cssText = 'border: 1px solid #e0e0e0; border-radius: 4px; padding: 2px 22px; font-size: 12px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; background-color: white; color: #666;';
            dismissButton.textContent = 'Cancel';
            dismissButton.addEventListener('click', dismissToast);

            buttonGroup.appendChild(primaryButton);
            buttonGroup.appendChild(dismissButton);
          }

          wrapper.appendChild(notificationContent);
          wrapper.appendChild(buttonGroup);

          toast.appendChild(wrapper);

          document.body.appendChild(toast);

          const style = document.createElement('style');
          style.textContent = '@keyframes fadeIn { from { opacity: 0; transform: translate(-50%, 20px); } to { opacity: 1; transform: translate(-50%, 0); } }' +
            '@keyframes fadeOut { from { opacity: 1; transform: translate(-50%, 0); } to { opacity: 0; transform: translate(-50%, 20px); } }';
          document.head.appendChild(style);
        }

        function showErrorRecoveryToast(reason) {
          const toast = document.getElementById(ERROR_RECOVERY_TOAST_ID);
          if (toast) {
            toast.style.animation = '';
            toast.offsetHeight;
            toast.style.animation = 'fadeIn 0.3s ease-in-out';
            toast.style.display = 'flex';
            return;
          }

          if (document.body) {
            createErrorRecoveryToast(reason);
          } else {
            // this script is injected inside the head tag
            // when errors happen, the document.body might not exist
            // use MutationObserver to wait for the body element shows up
            const observer = new MutationObserver(function() {
              if (document.body) {
                createErrorRecoveryToast(reason);
                observer.disconnect();
              }
            });
            observer.observe(document.documentElement, {childList: true});
          }
        }

        // make sure the first client js should clear this timeout
        window.__INDEX_JS_RESPONSE_TIMEOUT__ = setTimeout(() => {
          console.debug('Show toast because timeout');
          const reason = hasSyntaxError ? syntaxErrorMessage : '';
          showErrorRecoveryToast(reason);
        }, ERROR_RECOVERY_RESPONSE_TIMEOUT_MSEC)

        window.__showErrorRecoveryToast__ = showErrorRecoveryToast;
      })()
    </script>
    `;
};

export const clearErrorRecovery = () => {
  if (window.__INDEX_JS_RESPONSE_TIMEOUT__)
    clearTimeout(window.__INDEX_JS_RESPONSE_TIMEOUT__);

  const toast = document.getElementById(ERROR_RECOVERY_TOAST_ID);
  // Keep the browser-unsupported toast (marked persistent); only the
  // timeout / resource-error toasts should be cleared once the app boots.
  if (toast && toast.dataset.persistent !== '1') toast.style.display = 'none';
};
